// src/components/rodeoAi/ContextWarning.jsx
import React from 'react';
import { Alert, Button, Box, useMediaQuery, useTheme } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

const ContextWarning = ({ messageCount, onStartNew, onDismiss }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isUrgent = messageCount >= 30;

    return (
        <Alert
            severity={isUrgent ? 'error' : 'warning'}
            icon={<WarningIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            sx={{
                mb: { xs: 1.5, sm: 2 },
                backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                        ? isUrgent
                            ? 'rgba(244, 67, 54, 0.15)'
                            : 'rgba(255, 152, 0, 0.15)'
                        : isUrgent
                            ? 'rgba(244, 67, 54, 0.1)'
                            : 'rgba(255, 152, 0, 0.1)',
                backdropFilter: 'blur(10px)',
                border: (theme) =>
                    theme.palette.mode === 'dark'
                        ? isUrgent
                            ? '1px solid rgba(244, 67, 54, 0.3)'
                            : '1px solid rgba(255, 152, 0, 0.3)'
                        : isUrgent
                            ? '1px solid rgba(244, 67, 54, 0.2)'
                            : '1px solid rgba(255, 152, 0, 0.2)',
                '& .MuiAlert-message': {
                    width: '100%',
                    fontSize: { xs: '0.813rem', sm: '0.875rem' }
                }
            }}
        >
            <Box>
                <Box sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 0.5, sm: 0.5 },
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}>
                    {isUrgent ? '⚠️ Very Long Conversation' : '💡 Long Conversation Detected'}
                </Box>
                <Box sx={{ 
                    fontSize: { xs: '0.813rem', sm: '0.875rem' }, 
                    mb: { xs: 1, sm: 1.5 }
                }}>
                    {isMobile ? (
                        <>
                            {messageCount} messages. Consider starting fresh for better results.
                        </>
                    ) : (
                        <>
                            This conversation has {messageCount} messages. For best results, consider starting fresh.
                            <br />
                            Long conversations can cause:
                        </>
                    )}
                </Box>
                
                {!isMobile && (
                    <Box
                        component="ul"
                        sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            pl: { xs: 2, sm: 2 },
                            mb: { xs: 1, sm: 1.5 },
                            mt: 0,
                            '& li': { mb: { xs: 0.25, sm: 0.5 } }
                        }}
                    >
                        <li>Context confusion across multiple topics</li>
                        <li>Slower response times</li>
                        <li>Less accurate function calls</li>
                    </Box>
                )}
                
                <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.75, sm: 1 }, 
                    flexWrap: 'wrap' 
                }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={onStartNew}
                        sx={{
                            backgroundColor: isUrgent ? '#f44336' : '#ff9800',
                            fontSize: { xs: '0.75rem', sm: '0.813rem' },
                            px: { xs: 1.5, sm: 2 },
                            py: { xs: 0.5, sm: 0.75 },
                            minHeight: 0,
                            '&:hover': {
                                backgroundColor: isUrgent ? '#d32f2f' : '#f57c00'
                            }
                        }}
                    >
                        Start New
                    </Button>
                    {!isUrgent && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onDismiss}
                            sx={{
                                borderColor: 'currentColor',
                                color: 'inherit',
                                fontSize: { xs: '0.75rem', sm: '0.813rem' },
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 0.5, sm: 0.75 },
                                minHeight: 0
                            }}
                        >
                            Continue
                        </Button>
                    )}
                </Box>
            </Box>
        </Alert>
    );
};

export default ContextWarning;